import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from '@components/atoms/Popup';
import { Typography, TypographyAppearanceVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { ProductAttributesSelector } from '../ProductAttributesSelector';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useGetCartItemDetail } from '@services/useGetCartItemDetail';
import { useUpdateItemAttributesInCart } from '@services/useUpdateItemAttributesInCart';
import styles from './PopupModifyCartItem.module.css';

export interface PopupModifyCartItemProps {
    id?: string;
    show: boolean;
    onHide: () => void;
    cartItemUuid: string | null;
}

export const PopupModifyCartItem: FC<PopupModifyCartItemProps> = ({ id, show, onHide, cartItemUuid }) => {
    const { cartItemDetail } = useData();
    const { request: requestCartItemDetail, loading: loadingCartItemDetail } = useGetCartItemDetail();
    const { request: requestUpdateItemAttributesInCart, loading: loadingUpdateItemAttributesInCart } =
        useUpdateItemAttributesInCart();
    const { t, i18n } = useTranslation();

    const translations = {
        updateOnCart: t('popup-modify-cart-item-component.update-on-cart'),
        seeDetails: t('popup-modify-cart-item-component.see-details'),
    };

    // when cartItemId changes, update values for modal
    useEffect(() => {
        show && requestCartItemDetail({ uuid: cartItemUuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, i18n.language]);

    const [selectedAttributes, setSelectedAttributes] = useState<{ attributeUuid?: string; valueUuid?: string }[]>([]);

    useEffect(() => {
        setSelectedAttributes(cartItemDetail?.data?.selectedAttributes || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartItemDetail]);

    // hide modal after calling update endpoint
    useEffect(() => {
        !loadingUpdateItemAttributesInCart && show && onHide();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingUpdateItemAttributesInCart]);

    const productUuid = cartItemDetail?.data?.product?.uuid || '';
    const name = cartItemDetail?.data?.product?.name || '';
    const image = cartItemDetail?.data?.product?.image?.src || '';

    const handleOnChangeSelectedAttribute = (valueUuid: string, attributeUuid: string) => {
        setSelectedAttributes((previous) => {
            const otherFilters = previous.filter((item) => item.attributeUuid !== attributeUuid);
            return valueUuid ? [...otherFilters, { attributeUuid: `${attributeUuid}`, valueUuid: valueUuid }] : otherFilters;
        });
    };

    const handleOnUpdateCartItem = () => {
        requestUpdateItemAttributesInCart({
            cart_item_uuid: cartItemUuid || '',
            attributes: selectedAttributes,
        });
    };

    return (
        <Popup id={id || undefined} show={show} onHide={onHide} desktopWidth="1220px" fullWidthOnMobile={true}>
            <div className={styles['content-container']}>
                {!loadingCartItemDetail && (
                    <div className="row">
                        <div className="col-md-6">
                            {image && (
                                <div>
                                    <img src={image} className={styles.image} alt={name} />
                                </div>
                            )}
                        </div>
                        <div className="col-md-6">
                            <Space spaceBreakpoints={{ xs: { space: 50 } }} />
                            <Typography appearanceLevel={TypographyAppearanceVariants.level3}>{name}</Typography>
                            <hr />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            <ProductAttributesSelector
                                availableAttributes={cartItemDetail?.data?.availableAttributes || []}
                                selected={selectedAttributes}
                                onChange={handleOnChangeSelectedAttribute}
                            />
                            <div className="d-grid gap-3 pt-2 pt-md-0">
                                <Button
                                    mode={ButtonMode.primary}
                                    label={translations.updateOnCart}
                                    onClick={handleOnUpdateCartItem}
                                    disabled={loadingUpdateItemAttributesInCart}
                                />
                                <Anchor
                                    href={SITE_URLS.productDetail.replace(':uuid', `${productUuid}`)}
                                    mode={AnchorMode.primary}
                                    outline={true}>
                                    {translations.seeDetails}
                                </Anchor>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Popup>
    );
};
